import { useEffect, useRef } from "react";
import type { GameState } from "../../shared/game.ts";

type SoundKind = "move" | "capture" | "check" | "message" | "start" | "finish" | "enabled";

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  const AudioContextClass = window.AudioContext ??
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return null;
  audioContext ??= new AudioContextClass();
  return audioContext;
}

function note(context: AudioContext, frequency: number, start: number, duration: number, volume: number, type: OscillatorType = "sine") {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(volume, start + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

export async function playGameSound(kind: SoundKind): Promise<void> {
  const context = getAudioContext();
  if (!context || document.visibilityState !== "visible") return;
  if (context.state === "suspended") await context.resume().catch(() => undefined);
  if (context.state !== "running") return;
  const now = context.currentTime + 0.01;

  if (kind === "move") {
    note(context, 185, now, 0.07, 0.055, "triangle");
    note(context, 120, now + 0.035, 0.06, 0.035, "sine");
  } else if (kind === "capture") {
    note(context, 150, now, 0.08, 0.07, "square");
    note(context, 92, now + 0.045, 0.09, 0.05, "triangle");
  } else if (kind === "check") {
    note(context, 620, now, 0.1, 0.045, "sine");
    note(context, 830, now + 0.09, 0.14, 0.05, "sine");
  } else if (kind === "message") {
    note(context, 720, now, 0.09, 0.035, "sine");
    note(context, 960, now + 0.075, 0.13, 0.045, "sine");
  } else if (kind === "start" || kind === "enabled") {
    note(context, 392, now, 0.1, 0.035, "sine");
    note(context, 523, now + 0.08, 0.12, 0.04, "sine");
    note(context, 659, now + 0.16, 0.16, 0.045, "sine");
  } else {
    note(context, 523, now, 0.12, 0.04, "sine");
    note(context, 392, now + 0.1, 0.14, 0.04, "sine");
    note(context, 262, now + 0.2, 0.18, 0.045, "sine");
  }
}

export function useGameSounds(state: GameState | null, enabled: boolean): void {
  const previous = useRef<GameState | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const unlock = () => {
      const context = getAudioContext();
      if (context?.state === "suspended") void context.resume();
    };
    window.addEventListener("pointerdown", unlock, { capture: true, once: true });
    return () => window.removeEventListener("pointerdown", unlock, { capture: true });
  }, [enabled]);

  useEffect(() => {
    if (!state) return;
    const before = previous.current;
    previous.current = state;
    if (!before || !enabled) return;

    if (before.status === "waiting" && state.status === "active") {
      void playGameSound("start");
      return;
    }
    if (before.status !== "finished" && state.status === "finished") {
      void playGameSound("finish");
      return;
    }
    if (state.moves.length > before.moves.length) {
      const move = state.moves.at(-1);
      void playGameSound(state.check ? "check" : move?.san.includes("x") ? "capture" : "move");
    }
    if (state.messages.length > before.messages.length && state.messages.at(-1)?.side !== state.you) {
      void playGameSound("message");
    }
  }, [state, enabled]);
}
